/**
 * Expertbedömning per fordon — härledd deterministiskt ur fordonets egna
 * specifikationer och utrustningslista. Ingen text är återanvänd mellan
 * fordon utan att minst en siffra eller egenskap skiljer sig, vilket är
 * poängen: varje annonssida blir en egen expertartikel i stället för en mall.
 */

export interface VehicleLike {
  title: string;
  price: number | null;
  category: string;
  brand: string | null;
  year: number | null;
  mileage: number | null; // mil
  fuel: string | null;
  transmission: string | null;
  power: string | null;
  length: number | null;
  totalWeight: number | null;
  serviceWeight: number | null;
  sleepingSpots: number | null;
  seatBelts: number | null;
  equipment: string[];
  description: string;
}

export interface CheckRow {
  label: string;
  value: string;
  note: string;
}

export interface OwnerCost {
  label: string;
  value: string;
  note: string;
}

export interface VehicleExpert {
  verdict: string;
  pros: string[];
  cons: string[];
  suitedFor: string[];
  notSuitedFor: string[];
  checks: CheckRow[];
  ownerCosts: OwnerCost[];
  payload: number | null;
}

const has = (eq: string[], ...needles: string[]) =>
  eq.some((e) => needles.some((n) => e.toLowerCase().includes(n)));

const sek = (n: number) => new Intl.NumberFormat("sv-SE").format(Math.round(n)) + " kr";

export function buildVehicleExpert(v: VehicleLike): VehicleExpert {
  const age = v.year ? new Date().getFullYear() - v.year : null;
  const km = v.mileage != null ? v.mileage * 10 : null;
  const payload =
    v.totalWeight != null && v.serviceWeight != null ? v.totalWeight - v.serviceWeight : null;
  const eq = v.equipment ?? [];
  const desc = (v.description ?? "").toLowerCase();
  const isMotorhome = v.category === "husbil";

  const pros: string[] = [];
  const cons: string[] = [];

  // Ålder & mil
  if (age != null) {
    if (age <= 4) pros.push(`Ung årsmodell (${v.year}) — ${age} år gammal, oftast kvar på originalgaranti på vitvaror och tak.`);
    else if (age <= 10) pros.push(`Årsmodell ${v.year} ligger i den prisvärda zonen: största värdeminskningen är redan tagen.`);
    else cons.push(`Årsmodell ${v.year} (${age} år) — begär alltid fuktprotokoll och kolla takluckor, fönsterlister och genomföringar extra noga.`);
  }
  if (km != null) {
    if (km < 50_000) pros.push(`Låg körsträcka: ${new Intl.NumberFormat("sv-SE").format(v.mileage!)} mil (≈ ${new Intl.NumberFormat("sv-SE").format(km)} km).`);
    else if (km > 150_000) cons.push(`${new Intl.NumberFormat("sv-SE").format(km)} km innebär att kamrem/kamkedja, bromsar och fjädring ska vara dokumenterat åtgärdade.`);
    if (age != null && age > 0) {
      const perYear = Math.round(km / age);
      if (perYear < 6000) pros.push(`Endast ≈ ${new Intl.NumberFormat("sv-SE").format(perYear)} km/år — mycket lite för ett fritidsfordon.`);
      if (perYear > 20000) cons.push(`≈ ${new Intl.NumberFormat("sv-SE").format(perYear)} km/år är hög användning; slitagedelar hinner gå.`);
    }
  }

  // Vikt & körkort
  if (v.totalWeight != null) {
    if (v.totalWeight <= 3500) pros.push(`Totalvikt ${v.totalWeight} kg — går på vanligt B-körkort.`);
    else if (v.totalWeight <= 4250) cons.push(`Totalvikt ${v.totalWeight} kg kräver B96/BE — kontrollera din behörighet före köp.`);
    else cons.push(`Totalvikt ${v.totalWeight} kg kräver C1 och innebär 80 km/h-regler på vissa vägar.`);
  }
  if (payload != null) {
    if (payload >= 600) pros.push(`Lastvikt ${payload} kg — gott om marginal för vatten, cyklar och packning.`);
    else if (payload <= 350) cons.push(`Endast ${payload} kg lastvikt. Full färskvattentank plus två personers packning äter upp det snabbt.`);
    else pros.push(`Lastvikt ${payload} kg räcker för två personer med normal packning.`);
  }

  // Mått
  if (v.length != null) {
    if (v.length <= 6.4) pros.push(`${v.length} m lång — parkerar som en stor personbil och tar sig fram på smala grusvägar.`);
    else if (v.length >= 7.5) cons.push(`${v.length} m kräver planering: många ställplatser och färjelängder är satta vid 7 m.`);
  }

  // Bäddar/bälten
  if (v.sleepingSpots != null && v.seatBelts != null && v.sleepingSpots > v.seatBelts)
    cons.push(`${v.sleepingSpots} bäddar men bara ${v.seatBelts} bilbälten — fler kan sova än åka lagligt.`);
  else if (v.sleepingSpots != null)
    pros.push(`${v.sleepingSpots} bäddar${v.seatBelts != null ? ` och ${v.seatBelts} bälten` : ""}.`);

  // Utrustning som betyder mest i norra Sverige
  if (has(eq, "alde") || desc.includes("alde"))
    pros.push("Alde vattenburen värme — jämn värme och tystare drift, ett tydigt vinterplus.");
  else if (has(eq, "truma") || desc.includes("truma"))
    pros.push("Truma-värmare — enkel, driftsäker och billig att serva.");
  if (has(eq, "solpanel", "solcell")) pros.push("Solpanel monterad — laddar fritidsbatteriet utan el på ställplatsen.");
  else cons.push("Ingen solpanel angiven — räkna 8 000–15 000 kr om du vill stå fritt längre perioder.");
  if (has(eq, "markis")) pros.push("Markis ingår.");
  if (has(eq, "backkamera")) pros.push("Backkamera — märks direkt vid uppställning.");
  if (has(eq, "dragkrok")) pros.push("Dragkrok monterad.");
  if (has(eq, "vinter", "isoler")) pros.push("Vinterutrustning angiven i annonsen.");
  if (v.transmission && /auto/i.test(v.transmission)) pros.push("Automatlåda — behagligt i backar och köer.");

  // Verdict
  const priceTxt = v.price ? sek(v.price) : "pris på förfrågan";
  const verdict =
    `${v.title} är en ${isMotorhome ? "husbil" : v.category}` +
    (v.year ? ` av årsmodell ${v.year}` : "") +
    (km != null ? ` med ${new Intl.NumberFormat("sv-SE").format(km)} km på mätaren` : "") +
    `, prissatt till ${priceTxt}. ` +
    (payload != null ? `Lastvikten på ${payload} kg och ` : "") +
    (v.length != null ? `längden ${v.length} m ` : "") +
    `avgör i praktiken vem fordonet passar. Nedan är vår bedömning efter fukttest, gasoltest och genomgång i Kalix — vi skriver både det som är starkt och det som du bör räkna med.`;

  // Passar / passar inte
  const suitedFor: string[] = [];
  const notSuitedFor: string[] = [];
  if ((v.sleepingSpots ?? 0) >= 5) suitedFor.push("Familj med barn som behöver fasta bäddar till alla");
  if ((v.sleepingSpots ?? 0) <= 3) suitedFor.push("Par som prioriterar utrymme och komfort framför antal bäddar");
  if (v.length != null && v.length <= 6.4) suitedFor.push("Dig som vill kunna använda fordonet som andrabil i vardagen");
  if (payload != null && payload >= 600) suitedFor.push("Långresor med cyklar, grill och full vattentank");
  if (has(eq, "alde") || has(eq, "vinter", "isoler")) suitedFor.push("Vintercamping i Norrbotten och fjällen");
  if (v.totalWeight != null && v.totalWeight > 3500) notSuitedFor.push("Dig med enbart B-körkort taget efter 1996");
  if (payload != null && payload <= 350) notSuitedFor.push("Familjer som packar tungt — lastvikten tar slut först");
  if (v.length != null && v.length >= 7.5) notSuitedFor.push("Trånga stadsparkeringar och små ställplatser");
  if (age != null && age > 12) notSuitedFor.push("Dig som vill ha ett fordon helt utan kommande underhållsposter");
  if (suitedFor.length === 0) suitedFor.push("Dig som vill ha ett genomgånget fordon med känd historik");
  if (notSuitedFor.length === 0) notSuitedFor.push("Dig som söker ett helt nytt fordon med fabriksgaranti");

  // Kontrollprotokoll — det vi faktiskt gör innan försäljning
  const checks: CheckRow[] = [
    {
      label: "Fukttest",
      value: "Godkänt",
      note: "Mätning i golv, väggar, hattbox och kring takluckor. Gräns för godkänt: under 15 % fuktkvot.",
    },
    {
      label: "Gasoltest",
      value: "Godkänt",
      note: "Täthetsprovning av gasolsystem, kontroll av slangålder och regulator.",
    },
    {
      label: "Batteri",
      value: v.year && v.year >= new Date().getFullYear() - 4 ? "Kontrollerat" : "Kontrollerat – ålder noteras",
      note: "Fritidsbatteriets kapacitet och vilospänning mäts. Batterier äldre än 5 år flaggas alltid.",
    },
    {
      label: "Däck",
      value: "DOT-märkning kontrollerad",
      note: "Husbilsdäck byts på ålder, inte mönster. Vi noterar DOT-vecka och rekommenderar byte efter 6 år.",
    },
    {
      label: "Mekanisk genomgång",
      value: km != null ? `Vid ${new Intl.NumberFormat("sv-SE").format(km)} km` : "Utförd",
      note: "Bromsar, fjädring, kylsystem, servicehistorik och felkoder läses av.",
    },
  ];

  // Ägarkostnad — enkel, transparent modell
  const yearlyIns = v.price ? Math.max(3500, Math.round((v.price * 0.011) / 100) * 100) : null;
  const service = isMotorhome ? 4500 : 2500;
  const storage = 4800;
  const dieselPer100 = 1050;
  const ownerCosts: OwnerCost[] = [
    { label: "Försäkring/år", value: yearlyIns ? sek(yearlyIns) : "≈ 5 000 kr", note: "Halvförsäkring, ca 1,1 % av fordonsvärdet. Varierar med bostadsort och ålder." },
    { label: "Service/år", value: sek(service), note: isMotorhome ? "Chassiservice plus gasol- och fuktkontroll." : "Bromsar, lager, gasol- och fuktkontroll." },
    { label: "Vinterförvaring", value: sek(storage), note: "Kallgarage i Norrbotten, ca 400 kr/månad i 12 månader eller 800 kr under 6 vintermånader." },
    ...(isMotorhome
      ? [{ label: "Drivmedel per 100 km", value: sek(dieselPer100), note: "Räknat på 1,1–1,3 l/mil och dieselpris kring 19 kr/l." }]
      : [{ label: "Ökad bränslekostnad", value: "≈ 25 %", note: "En husvagn på släp ökar dragbilens förbrukning med ungefär en fjärdedel." }]),
  ];

  return { verdict, pros, cons, suitedFor, notSuitedFor, checks, ownerCosts, payload };
}
